import React, { useState } from 'react';
import {
  usePredictiveModConfig,
  usePredictiveModStatus,
  usePredictiveModLogs,
  useTogglePredictiveModControl,
  useUpdatePredictiveModConfig,
  useCheckPredictiveModState,
} from '../hooks/usePredictiveModControl';

// ==================== SVG ICONS ====================

const DiceIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="2" width="20" height="20" rx="3"/>
    <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
    <circle cx="16" cy="8" r="1.5" fill="currentColor"/>
    <circle cx="8" cy="16" r="1.5" fill="currentColor"/>
    <circle cx="16" cy="16" r="1.5" fill="currentColor"/>
    <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

const PercentIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 20V10M12 20V4M6 20v-6"/>
  </svg>
);

const PowerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

// ==================== STYLES ====================

const styles = `
  .pmc-panel {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }

  .pmc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%);
    color: #fff;
  }

  .pmc-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .pmc-header h3 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .pmc-badge {
    background: rgba(255,255,255,0.2);
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 500;
  }

  .pmc-toggle {
    position: relative;
    width: 48px;
    height: 24px;
  }

  .pmc-toggle input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .pmc-toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255,255,255,0.3);
    border-radius: 24px;
    transition: 0.2s;
  }

  .pmc-toggle-slider:before {
    content: "";
    position: absolute;
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background: #fff;
    border-radius: 50%;
    transition: 0.2s;
  }

  .pmc-toggle input:checked + .pmc-toggle-slider {
    background: #22c55e;
  }

  .pmc-toggle input:checked + .pmc-toggle-slider:before {
    transform: translateX(24px);
  }

  .pmc-toggle input:disabled + .pmc-toggle-slider {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .pmc-content {
    padding: 16px;
  }

  .pmc-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;
  }

  @media (max-width: 900px) {
    .pmc-grid { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 500px) {
    .pmc-grid { grid-template-columns: 1fr; }
  }

  .pmc-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px;
    text-align: center;
  }

  .pmc-card-label {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .pmc-card-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e3a8a;
    margin-bottom: 4px;
  }

  .pmc-card-value.blue { color: #2563eb; }
  .pmc-card-value.green { color: #22c55e; }
  .pmc-card-value.amber { color: #f59e0b; }
  .pmc-card-value.red { color: #ef4444; }

  .pmc-card-sub {
    font-size: 0.7rem;
    color: #94a3b8;
  }

  .pmc-card.active {
    background: #f0fdf4;
    border-color: #22c55e;
  }

  .pmc-card.inactive {
    background: #fef2f2;
    border-color: #fecaca;
  }

  .pmc-status-indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .pmc-status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .pmc-status-dot.active { background: #22c55e; }
  .pmc-status-dot.inactive { background: #ef4444; }

  .pmc-progress {
    margin-bottom: 16px;
  }

  .pmc-progress-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 6px;
  }

  .pmc-progress-bar {
    height: 8px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
  }

  .pmc-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .pmc-progress-fill.complete {
    background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
  }

  .pmc-decision {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #3b82f6;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 16px;
  }

  .pmc-decision.activate { border-left-color: #22c55e; background: #f0fdf4; }
  .pmc-decision.deactivate { border-left-color: #ef4444; background: #fef2f2; }
  .pmc-decision.nochange { border-left-color: #94a3b8; }

  .pmc-decision-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  .pmc-decision-label {
    font-size: 0.75rem;
    color: #64748b;
  }

  .pmc-decision-value {
    font-size: 0.8rem;
    font-weight: 600;
    color: #1e293b;
  }

  .pmc-decision-reason {
    font-size: 0.7rem;
    color: #64748b;
  }

  .pmc-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 16px;
    border: none;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .pmc-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .pmc-btn-primary {
    background: #2563eb;
    color: #fff;
  }

  .pmc-btn-primary:hover:not(:disabled) {
    background: #1e40af;
  }

  .pmc-btn-secondary {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
  }

  .pmc-btn-secondary:hover:not(:disabled) {
    background: #e2e8f0;
  }

  .pmc-btn-center {
    display: block;
    width: 100%;
    max-width: 300px;
    margin: 0 auto 16px;
  }

  .pmc-section {
    margin-bottom: 12px;
  }

  .pmc-section-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #1e293b;
    cursor: pointer;
    transition: all 0.2s;
  }

  .pmc-section-toggle:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }

  .pmc-section-content {
    margin-top: 12px;
    padding: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
  }

  .pmc-threshold-table {
    width: 100%;
    margin-bottom: 12px;
  }

  .pmc-threshold-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 8px 12px;
    border-bottom: 1px solid #e2e8f0;
    align-items: center;
    font-size: 0.8rem;
  }

  .pmc-threshold-row.header {
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 4px 4px 0 0;
  }

  .pmc-threshold-level {
    font-weight: 500;
    color: #1e293b;
  }

  .pmc-threshold-value {
    font-weight: 600;
    color: #2563eb;
    text-align: right;
  }

  .pmc-threshold-row input {
    width: 70px;
    padding: 6px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    text-align: center;
    font-size: 0.8rem;
    margin-left: auto;
  }

  .pmc-threshold-row input:focus {
    outline: none;
    border-color: #3b82f6;
  }

  .pmc-interval {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: #f1f5f9;
    border-radius: 4px;
    margin-bottom: 12px;
    font-size: 0.8rem;
  }

  .pmc-interval label {
    color: #64748b;
    flex: 1;
  }

  .pmc-interval span {
    font-weight: 600;
    color: #2563eb;
  }

  .pmc-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .pmc-logs-content {
    max-height: 300px;
    overflow-y: auto;
  }

  .pmc-log-table {
    width: 100%;
    font-size: 0.75rem;
  }

  .pmc-log-row {
    display: grid;
    grid-template-columns: 2fr 1fr 0.7fr 0.7fr 0.7fr 1fr;
    padding: 6px 8px;
    border-bottom: 1px solid #e2e8f0;
    align-items: center;
  }

  .pmc-log-row.header {
    font-weight: 600;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 4px 4px 0 0;
  }

  .pmc-log-row.activate { background: #f0fdf4; }
  .pmc-log-row.deactivate { background: #fef2f2; }

  .pmc-log-time { color: #64748b; }
  .pmc-log-action { font-weight: 500; }
  .pmc-log-num { text-align: center; }
  .pmc-log-decision { text-align: right; font-weight: 600; }

  .pmc-empty {
    padding: 24px;
    text-align: center;
    color: #64748b;
    font-size: 0.85rem;
  }

  .pmc-disabled {
    padding: 32px;
    text-align: center;
    color: #64748b;
  }

  .pmc-disabled p {
    margin: 8px 0;
    font-size: 0.85rem;
  }

  .pmc-disabled p:first-child {
    font-weight: 500;
    color: #1e293b;
  }

  .pmc-loading {
    padding: 32px;
    text-align: center;
    color: #64748b;
    font-size: 0.85rem;
  }
`;

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatTime = (date: Date | string | null) => {
  if (!date) return 'Nunca';
  const d = new Date(date);
  return d.toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDateTime = (date: Date | string | null) => {
  if (!date) return 'Nunca';
  const d = new Date(date);
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const PredictiveModControlPanel: React.FC = () => {
  const { config, loading: configLoading, refetch: refetchConfig } = usePredictiveModConfig();
  const { status, loading: _statusLoading } = usePredictiveModStatus(3000);
  const { logs, loading: logsLoading, refetch: refetchLogs } = usePredictiveModLogs(20);
  const { toggle, toggling } = useTogglePredictiveModControl();
  const { updateConfig, updating } = useUpdatePredictiveModConfig();
  const { check, checking } = useCheckPredictiveModState();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [editingThresholds, setEditingThresholds] = useState(false);
  const [thresholds, setThresholds] = useState({
    threshold100Probability: 95,
    threshold90Probability: 70,
    threshold70Probability: 40,
    threshold50Probability: 15,
    thresholdBelowProbability: 5,
    checkIntervalSeconds: 3,
  });

  React.useEffect(() => {
    if (config) {
      setThresholds({
        threshold100Probability: config.threshold100Probability,
        threshold90Probability: config.threshold90Probability,
        threshold70Probability: config.threshold70Probability,
        threshold50Probability: config.threshold50Probability,
        thresholdBelowProbability: config.thresholdBelowProbability,
        checkIntervalSeconds: config.checkIntervalSeconds,
      });
    }
  }, [config]);

  const handleToggle = async () => {
    if (!config) return;
    try {
      await toggle(!config.enabled, 'admin');
    } catch (error) {
      console.error('Error toggling predictive control:', error);
    }
  };

  const handleSaveThresholds = async () => {
    try {
      await updateConfig({
        ...thresholds,
        changedBy: 'admin',
      });
      setEditingThresholds(false);
      refetchConfig();
    } catch (error) {
      console.error('Error updating thresholds:', error);
    }
  };

  const handleManualCheck = async () => {
    try {
      const result = await check();
      console.log('Manual check result:', result);
      refetchLogs();
    } catch (error) {
      console.error('Error running manual check:', error);
    }
  };

  const getDecisionClass = (decision: string | null) => {
    switch (decision) {
      case 'ACTIVATE': return 'activate';
      case 'DEACTIVATE': return 'deactivate';
      case 'NO_CHANGE': return 'nochange';
      default: return '';
    }
  };

  const getThresholdLabel = (level: string) => {
    switch (level) {
      case '>=100%': return 'Muy Alto';
      case '90-99%': return 'Alto';
      case '70-89%': return 'Medio';
      case '50-69%': return 'Bajo';
      case '<50%': return 'Muy Bajo';
      default: return level;
    }
  };

  const getPercentageColor = (pct: number) => {
    if (pct >= 100) return 'green';
    if (pct >= 70) return 'amber';
    return 'red';
  };

  if (configLoading && !config) {
    return (
      <div className="pmc-panel">
        <style>{styles}</style>
        <div className="pmc-loading">Cargando configuracion...</div>
      </div>
    );
  }

  return (
    <div className="pmc-panel">
      <style>{styles}</style>

      <div className="pmc-header">
        <div className="pmc-header-left">
          <DiceIcon />
          <h3>Control Predictivo del MOD</h3>
          <span className="pmc-badge">Facturacion Electronica</span>
        </div>
        <label className="pmc-toggle">
          <input
            type="checkbox"
            checked={config?.enabled || false}
            onChange={handleToggle}
            disabled={toggling}
          />
          <span className="pmc-toggle-slider"></span>
        </label>
      </div>

      <div className="pmc-content">
        {config?.enabled && status && (
          <>
            {/* Status Grid */}
            <div className="pmc-grid">
              <div className="pmc-card">
                <div className="pmc-card-label"><DollarIcon /> Impuestos Hora Actual</div>
                <div className="pmc-card-value blue">{formatCurrency(status.currentTaxAmount)}</div>
                <div className="pmc-card-sub">Meta: {formatCurrency(status.currentTaxTarget)}</div>
              </div>

              <div className="pmc-card">
                <div className="pmc-card-label"><PercentIcon /> Porcentaje</div>
                <div className={`pmc-card-value ${getPercentageColor(status.currentPercentage)}`}>
                  {status.currentPercentage.toFixed(1)}%
                </div>
                <div className="pmc-card-sub">Nivel: {getThresholdLabel(status.thresholdLevel)}</div>
              </div>

              <div className="pmc-card">
                <div className="pmc-card-label"><ChartIcon /> Probabilidad MOD</div>
                <div className="pmc-card-value blue">{status.probabilityToActivate}%</div>
                <div className="pmc-card-sub">de activar MOD</div>
              </div>

              <div className={`pmc-card ${status.modEnabled ? 'active' : 'inactive'}`}>
                <div className="pmc-card-label"><PowerIcon /> Estado MOD</div>
                <div className="pmc-card-value">
                  <span className="pmc-status-indicator">
                    <span className={`pmc-status-dot ${status.modEnabled ? 'active' : 'inactive'}`}></span>
                    {status.modEnabled ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </div>
                <div className="pmc-card-sub">Ultima: {formatTime(status.lastCheckAt)}</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="pmc-progress">
              <div className="pmc-progress-header">
                <span>Progreso hacia la meta</span>
                <span>{status.currentPercentage.toFixed(1)}%</span>
              </div>
              <div className="pmc-progress-bar">
                <div
                  className={`pmc-progress-fill ${status.currentPercentage >= 100 ? 'complete' : ''}`}
                  style={{ width: `${Math.min(status.currentPercentage, 100)}%` }}
                />
              </div>
            </div>

            {/* Last Decision */}
            {status.lastDecision && (
              <div className={`pmc-decision ${getDecisionClass(status.lastDecision)}`}>
                <div className="pmc-decision-header">
                  <span className="pmc-decision-label">Ultima Decision:</span>
                  <span className="pmc-decision-value">{status.lastDecision}</span>
                </div>
                {status.lastReason && (
                  <div className="pmc-decision-reason">{status.lastReason}</div>
                )}
              </div>
            )}

            {/* Manual Check Button */}
            <button
              className="pmc-btn pmc-btn-primary pmc-btn-center"
              onClick={handleManualCheck}
              disabled={checking}
            >
              <DiceIcon /> {checking ? 'Verificando...' : 'Ejecutar Verificacion Manual'}
            </button>

            {/* Advanced Configuration */}
            <div className="pmc-section">
              <button
                className="pmc-section-toggle"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? <ChevronDownIcon /> : <ChevronRightIcon />}
                <SettingsIcon /> Configuracion de Probabilidades
              </button>

              {showAdvanced && (
                <div className="pmc-section-content">
                  <div className="pmc-threshold-table">
                    <div className="pmc-threshold-row header">
                      <span>Porcentaje de Meta</span>
                      <span>Probabilidad MOD</span>
                    </div>
                    <div className="pmc-threshold-row">
                      <span className="pmc-threshold-level">&ge; 100%</span>
                      {editingThresholds ? (
                        <input
                          type="number"
                          value={thresholds.threshold100Probability}
                          onChange={(e) => setThresholds({ ...thresholds, threshold100Probability: parseInt(e.target.value) || 0 })}
                          min="0" max="100"
                        />
                      ) : (
                        <span className="pmc-threshold-value">{config?.threshold100Probability}%</span>
                      )}
                    </div>
                    <div className="pmc-threshold-row">
                      <span className="pmc-threshold-level">90 - 99%</span>
                      {editingThresholds ? (
                        <input
                          type="number"
                          value={thresholds.threshold90Probability}
                          onChange={(e) => setThresholds({ ...thresholds, threshold90Probability: parseInt(e.target.value) || 0 })}
                          min="0" max="100"
                        />
                      ) : (
                        <span className="pmc-threshold-value">{config?.threshold90Probability}%</span>
                      )}
                    </div>
                    <div className="pmc-threshold-row">
                      <span className="pmc-threshold-level">70 - 89%</span>
                      {editingThresholds ? (
                        <input
                          type="number"
                          value={thresholds.threshold70Probability}
                          onChange={(e) => setThresholds({ ...thresholds, threshold70Probability: parseInt(e.target.value) || 0 })}
                          min="0" max="100"
                        />
                      ) : (
                        <span className="pmc-threshold-value">{config?.threshold70Probability}%</span>
                      )}
                    </div>
                    <div className="pmc-threshold-row">
                      <span className="pmc-threshold-level">50 - 69%</span>
                      {editingThresholds ? (
                        <input
                          type="number"
                          value={thresholds.threshold50Probability}
                          onChange={(e) => setThresholds({ ...thresholds, threshold50Probability: parseInt(e.target.value) || 0 })}
                          min="0" max="100"
                        />
                      ) : (
                        <span className="pmc-threshold-value">{config?.threshold50Probability}%</span>
                      )}
                    </div>
                    <div className="pmc-threshold-row">
                      <span className="pmc-threshold-level">&lt; 50%</span>
                      {editingThresholds ? (
                        <input
                          type="number"
                          value={thresholds.thresholdBelowProbability}
                          onChange={(e) => setThresholds({ ...thresholds, thresholdBelowProbability: parseInt(e.target.value) || 0 })}
                          min="0" max="100"
                        />
                      ) : (
                        <span className="pmc-threshold-value">{config?.thresholdBelowProbability}%</span>
                      )}
                    </div>
                  </div>

                  <div className="pmc-interval">
                    <ClockIcon />
                    <label>Intervalo de verificacion:</label>
                    {editingThresholds ? (
                      <input
                        type="number"
                        value={thresholds.checkIntervalSeconds}
                        onChange={(e) => setThresholds({ ...thresholds, checkIntervalSeconds: parseInt(e.target.value) || 3 })}
                        min="1" max="60"
                        style={{ width: '60px' }}
                      />
                    ) : (
                      <span>{config?.checkIntervalSeconds}s</span>
                    )}
                  </div>

                  <div className="pmc-actions">
                    {editingThresholds ? (
                      <>
                        <button
                          className="pmc-btn pmc-btn-primary"
                          onClick={handleSaveThresholds}
                          disabled={updating}
                        >
                          {updating ? 'Guardando...' : 'Guardar'}
                        </button>
                        <button
                          className="pmc-btn pmc-btn-secondary"
                          onClick={() => {
                            setEditingThresholds(false);
                            if (config) {
                              setThresholds({
                                threshold100Probability: config.threshold100Probability,
                                threshold90Probability: config.threshold90Probability,
                                threshold70Probability: config.threshold70Probability,
                                threshold50Probability: config.threshold50Probability,
                                thresholdBelowProbability: config.thresholdBelowProbability,
                                checkIntervalSeconds: config.checkIntervalSeconds,
                              });
                            }
                          }}
                        >
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <button
                        className="pmc-btn pmc-btn-secondary"
                        onClick={() => setEditingThresholds(true)}
                      >
                        Editar Probabilidades
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Logs Section */}
            <div className="pmc-section">
              <button
                className="pmc-section-toggle"
                onClick={() => {
                  setShowLogs(!showLogs);
                  if (!showLogs) refetchLogs();
                }}
              >
                {showLogs ? <ChevronDownIcon /> : <ChevronRightIcon />}
                <ClockIcon /> Historial de Decisiones ({logs.length})
              </button>

              {showLogs && (
                <div className="pmc-section-content">
                  {logsLoading ? (
                    <div className="pmc-empty">Cargando logs...</div>
                  ) : logs.length === 0 ? (
                    <div className="pmc-empty">No hay decisiones registradas</div>
                  ) : (
                    <div className="pmc-logs-content">
                      <div className="pmc-log-table">
                        <div className="pmc-log-row header">
                          <span>Fecha/Hora</span>
                          <span>Accion</span>
                          <span>%</span>
                          <span>Prob</span>
                          <span>Random</span>
                          <span>Decision</span>
                        </div>
                        {logs.map((log) => (
                          <div key={log.id} className={`pmc-log-row ${getDecisionClass(log.decision)}`}>
                            <span className="pmc-log-time">{formatDateTime(log.createdAt)}</span>
                            <span className="pmc-log-action">{log.action}</span>
                            <span className="pmc-log-num">{log.taxPercentage?.toFixed(1) || '-'}%</span>
                            <span className="pmc-log-num">{log.probabilityUsed || '-'}%</span>
                            <span className="pmc-log-num">{log.randomValue?.toFixed(1) || '-'}</span>
                            <span className="pmc-log-decision">{log.decision || '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {!config?.enabled && (
          <div className="pmc-disabled">
            <p>El control predictivo esta desactivado.</p>
            <p>Activa el toggle para comenzar a usar el control probabilistico del MOD basado en facturacion electronica.</p>
          </div>
        )}
      </div>
    </div>
  );
};
